import { searchClient } from '@/lib/search/instantsearch/algoliaAdapter';
import { Suggestion } from './types';

/**
 * Query Algolia for display suggestions based on user input
 * Returns actual display names, locations, and holidays from the index
 */
export async function getAlgoliaDisplaySuggestions(query: string): Promise<Suggestion[]> {
  if (!searchClient) {
    console.warn('Algolia client not available for autocomplete');
    return [];
  }

  if (!query || query.trim().length < 2) {
    // Return popular suggestions when query is empty or too short
    return [
      { label: 'Christmas Displays', value: 'Christmas', type: 'holiday' },
      { label: 'Halloween Displays', value: 'Halloween', type: 'holiday' },
      { label: '4th of July Displays', value: '4th of July', type: 'holiday' },
      { label: 'Easter Displays', value: 'Easter', type: 'holiday' },
      { label: 'Thanksgiving Displays', value: 'Thanksgiving', type: 'holiday' },
    ];
  }

  try {
    // Query the displays index
    const { results } = await searchClient.search([
      {
        indexName: 'displays',
        query: query.trim(),
        params: {
          hitsPerPage: 8,
          attributesToRetrieve: ['display_name', 'city', 'state', 'holiday_type', 'featured'],
          attributesToHighlight: [],
        },
      },
    ]);

    const hits = results[0]?.hits || [];
    const suggestions: Suggestion[] = [];

    // Debug: Log first hit to see what fields are available
    if (hits.length > 0) {
      console.log('First Algolia display hit:', hits[0]);
    }

    // Transform Algolia hits to suggestions
    hits.forEach((hit: any) => {
      // Add display name with location
      if (hit.display_name) {
        const locationSuffix = hit.city && hit.state
          ? ` - ${hit.city}, ${hit.state}`
          : hit.city
            ? ` - ${hit.city}`
            : hit.state
              ? ` - ${hit.state}`
              : '';

        suggestions.push({
          label: `${hit.display_name}${locationSuffix}`,
          value: hit.display_name,
          type: 'display',
          displayId: hit.objectID,
        });
      }
    });

    return suggestions.slice(0, 8); // Limit to 8 suggestions
  } catch (error) {
    console.error('Algolia display autocomplete error:', error);
    return [];
  }
}

/**
 * Get total count of displays matching the query for "See all X displays" footer
 */
export async function getAlgoliaDisplayCount(query: string): Promise<number> {
  if (!searchClient || !query || query.trim().length < 2) {
    return 0;
  }

  try {
    const { results } = await searchClient.search([
      {
        indexName: 'displays',
        query: query.trim(),
        params: {
          hitsPerPage: 0, // We only need the count
          attributesToRetrieve: [],
        },
      },
    ]);

    return results[0]?.nbHits || 0;
  } catch (error) {
    console.error('Algolia display count error:', error);
    return 0;
  }
}
